import React, { useState, useEffect, useRef } from 'react';
import { jobService } from '../../services/jobService';

const DEPARTMENT_LABELS = {
  'kinh-doanh': 'Kinh doanh',
  'marketing': 'Marketing',
  'hcns': 'Hành chính Nhân sự',
  'ke-toan': 'Kế toán',
  'kho-van': 'Kho vận',
  'thu-mua': 'Thu mua',
  'chuyen-mon': 'Chuyên môn (Trợ lý, R&D)'
};
const DEFAULT_DEPARTMENT_ORDER = ['kinh-doanh', 'marketing', 'hcns', 'ke-toan', 'kho-van', 'thu-mua', 'chuyen-mon'];

const JOB_ICONS = {
  briefcase: <><path d="M3 21l3-3 5 5" /><path d="M14 3l7 7-9 9H5v-7l9-9z" /></>,
  users: <><circle cx="9" cy="7" r="4" /><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /><path d="M21 21v-2a4 4 0 0 0-3-3.87" /></>,
  calendar: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M8 2v4M16 2v4M3 10h18" /></>,
  building: <><path d="M3 10l9-7 9 7v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V10z" /><path d="M9 21V12h6v9" /></>,
  chart: <><path d="M3 3v18h18" /><path d="M7 14l4-4 4 4 5-6" /></>,
  chat: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  file: <><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 7h8M8 11h8M8 15h5" /></>,
  heart: <path d="M12 21s-7-4.35-9.5-9A5.5 5.5 0 0 1 12 6a5.5 5.5 0 0 1 9.5 6c-2.5 4.65-9.5 9-9.5 9z" fill="currentColor" stroke="none" />
};

const EMPTY_FORM = {
  title: '',
  key: '',
  department: 'kinh-doanh',
  headcount: 1,
  salary: '',
  icon_key: 'briefcase',
  is_active: true,
  objectives: '',
  responsibilities: '',
  requirements: '',
  benefits: ''
};

export default function JobsManager() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [dragOverId, setDragOverId] = useState(null);
  const dragSrcId = useRef(null);
  const reorderBusy = useRef(false);

  const [deptOrder, setDeptOrder] = useState(DEFAULT_DEPARTMENT_ORDER);
  const [dragOverDept, setDragOverDept] = useState(null);
  const dragSrcDept = useRef(null);
  const deptReorderBusy = useRef(false);

  const loadJobs = () => {
    setLoading(true);
    jobService
      .getAllJobsAdmin()
      .then((res) => {
        if (res.data) setJobs(res.data);
      })
      .catch((err) => alert(err.message))
      .finally(() => setLoading(false));
  };

  const loadDeptOrder = () => {
    jobService
      .getDepartmentOrder()
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length) setDeptOrder(res.data);
      })
      .catch(() => {});
  };

  useEffect(() => {
    loadJobs();
    loadDeptOrder();
  }, []);

  const handleOpenAdd = () => {
    setEditingJob(null);
    setFormData(EMPTY_FORM);
    setModalOpen(true);
  };

  const handleOpenEdit = (job) => {
    setEditingJob(job);
    setFormData({
      title: job.title || '',
      key: job.key || '',
      department: job.department || 'kinh-doanh',
      headcount: job.headcount || 1,
      salary: job.salary || '',
      icon_key: job.icon_key || 'briefcase',
      is_active: job.is_active ?? true,
      objectives: Array.isArray(job.objectives) ? job.objectives.join('\n') : '',
      responsibilities: Array.isArray(job.responsibilities) ? job.responsibilities.join('\n') : '',
      requirements: Array.isArray(job.requirements) ? job.requirements.join('\n') : '',
      benefits: Array.isArray(job.benefits) ? job.benefits.join('\n') : ''
    });
    setModalOpen(true);
  };

  const handleDelete = async (id, title) => {
    if (window.confirm(`Xoá vị trí "${title}"?\n\nHành động không thể hoàn tác.`)) {
      try {
        await jobService.deleteJob(id);
        loadJobs();
      } catch (err) {
        alert(err.message);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      headcount: Number(formData.headcount) || 1,
      objectives: formData.objectives.split('\n').map((s) => s.trim()).filter(Boolean),
      responsibilities: formData.responsibilities.split('\n').map((s) => s.trim()).filter(Boolean),
      requirements: formData.requirements.split('\n').map((s) => s.trim()).filter(Boolean),
      benefits: formData.benefits.split('\n').map((s) => s.trim()).filter(Boolean)
    };

    try {
      if (editingJob) {
        await jobService.updateJob(editingJob.id, payload);
      } else {
        await jobService.createJob(payload);
      }
      setModalOpen(false);
      loadJobs();
    } catch (err) {
      alert(err.message);
    }
  };

  // ---------------- Drag & drop reorder ----------------
  const handleDragStart = (e, id) => {
    dragSrcId.current = id;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
  };
  const handleDragEnd = () => {
    dragSrcId.current = null;
    setDragOverId(null);
  };
  const handleDragOver = (e, id) => {
    if (!dragSrcId.current || dragSrcId.current === id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverId(id);
  };
  const handleDrop = async (e, dstId) => {
    e.preventDefault();
    setDragOverId(null);
    const srcId = dragSrcId.current;
    if (!srcId || srcId === dstId || reorderBusy.current) return;

    const srcIdx = jobs.findIndex((j) => j.id === srcId);
    const dstIdx = jobs.findIndex((j) => j.id === dstId);
    if (srcIdx < 0 || dstIdx < 0) return;

    const reordered = jobs.slice();
    const [moved] = reordered.splice(srcIdx, 1);
    reordered.splice(dstIdx, 0, moved);

    const updates = [];
    reordered.forEach((j, i) => {
      const newSort = i + 1;
      if (j.sort_order !== newSort) {
        updates.push({ id: j.id, sort_order: newSort });
        j.sort_order = newSort;
      }
    });
    setJobs(reordered);
    if (!updates.length) return;

    reorderBusy.current = true;
    try {
      await jobService.reorderJobs(updates);
    } catch (err) {
      alert('Lỗi đổi thứ tự: ' + err.message);
      loadJobs();
    } finally {
      reorderBusy.current = false;
    }
  };

  // ---------------- Drag & drop reorder — cả nhóm phòng ban ----------------
  const handleGroupDragStart = (e, dept) => {
    dragSrcDept.current = dept;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', dept);
  };
  const handleGroupDragEnd = () => {
    dragSrcDept.current = null;
    setDragOverDept(null);
  };
  const handleGroupDragOver = (e, dept) => {
    if (!dragSrcDept.current || dragSrcDept.current === dept) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDept(dept);
  };
  const handleGroupDrop = async (e, dstDept) => {
    e.preventDefault();
    setDragOverDept(null);
    const srcDept = dragSrcDept.current;
    if (!srcDept || srcDept === dstDept || deptReorderBusy.current) return;

    const srcIdx = deptOrder.indexOf(srcDept);
    const dstIdx = deptOrder.indexOf(dstDept);
    if (srcIdx < 0 || dstIdx < 0) return;

    const reordered = deptOrder.slice();
    const [moved] = reordered.splice(srcIdx, 1);
    reordered.splice(dstIdx, 0, moved);
    setDeptOrder(reordered);

    deptReorderBusy.current = true;
    try {
      await jobService.updateDepartmentOrder(reordered);
    } catch (err) {
      alert('Lỗi đổi thứ tự phòng ban: ' + err.message);
      loadDeptOrder();
    } finally {
      deptReorderBusy.current = false;
    }
  };

  // ---------------- Grouping by department ----------------
  const groups = {};
  jobs.forEach((j) => {
    const d = j.department || 'khac';
    (groups[d] = groups[d] || []).push(j);
  });
  const orderedDepts = [...deptOrder, ...Object.keys(groups).filter((d) => !deptOrder.includes(d))].filter((d) => groups[d]);

  let globalIdx = 0;

  return (
    <section className="admin-panel">
      <div className="panel-head">
        <div>
          <h2>Vị trí tuyển dụng</h2>
          <p className="panel-head__sub">
            Quản lý vị trí hiển thị trên <code>/tuyen-dung</code> + <code>/welcome</code>. Kéo–thả hàng để đổi thứ tự vị trí, kéo–thả tiêu đề nhóm để đổi thứ tự phòng ban (số bên trái = thứ tự hiện tại).
          </p>
        </div>
        <button className="btn btn--primary" onClick={handleOpenAdd} type="button">
          + Thêm vị trí
        </button>
      </div>

      {loading ? (
        <p className="muted">Đang tải…</p>
      ) : jobs.length === 0 ? (
        <p className="muted">Chưa có vị trí nào. Bấm <strong>+ Thêm vị trí</strong> để tạo mới.</p>
      ) : (
        <div className="jobs-list">
          {orderedDepts.map((d) => {
            const label = DEPARTMENT_LABELS[d] || d;
            return (
              <React.Fragment key={d}>
                <h3
                  className={`job-group-head job-group-head--draggable ${dragSrcDept.current === d ? 'is-dragging' : ''} ${dragOverDept === d ? 'is-drag-over' : ''}`}
                  draggable="true"
                  title="Kéo để đổi thứ tự phòng ban trên trang Tuyển dụng & Welcome"
                  onDragStart={(e) => handleGroupDragStart(e, d)}
                  onDragEnd={handleGroupDragEnd}
                  onDragOver={(e) => handleGroupDragOver(e, d)}
                  onDragLeave={() => setDragOverDept((prev) => (prev === d ? null : prev))}
                  onDrop={(e) => handleGroupDrop(e, d)}
                >
                  <span className="job-group-head__handle" aria-hidden="true">⋮⋮</span>
                  {label} <span className="job-group-count">{groups[d].length}</span>
                </h3>
                {groups[d].map((job) => {
                  globalIdx++;
                  const idx = globalIdx;
                  const iconSvg = JOB_ICONS[job.icon_key] || JOB_ICONS.briefcase;
                  return (
                    <div
                      key={job.id}
                      className={`job-row job-row--draggable ${job.is_active ? '' : 'is-inactive'} ${dragSrcId.current === job.id ? 'is-dragging' : ''} ${dragOverId === job.id ? 'is-drag-over' : ''}`}
                      draggable="true"
                      onDragStart={(e) => handleDragStart(e, job.id)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => handleDragOver(e, job.id)}
                      onDragLeave={() => setDragOverId((prev) => (prev === job.id ? null : prev))}
                      onDrop={(e) => handleDrop(e, job.id)}
                    >
                      <span className="job-row__handle" aria-label="Kéo để đổi thứ tự" title={`Kéo để đổi thứ tự (trong phòng ${label})`}>⋮⋮</span>
                      <span className="job-row__index" aria-hidden="true">{idx}</span>
                      <button
                        type="button"
                        className="job-row__icon"
                        title="Click để sửa nhanh"
                        aria-label="Sửa vị trí"
                        onClick={() => handleOpenEdit(job)}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          {iconSvg}
                        </svg>
                      </button>
                      <div className="job-row__main">
                        <span className="job-row__title">
                          {job.title}
                          <span className={`job-row__badge ${job.is_active ? '' : 'job-row__badge--off'}`}>
                            {job.is_active ? 'Đang hiện' : 'Đã ẩn'}
                          </span>
                          {job.headcount > 0 && (
                            <span className="job-row__badge job-row__badge--count">Tuyển {job.headcount} người</span>
                          )}
                        </span>
                        <span className="job-row__meta">key: <code>{job.key}</code></span>
                      </div>
                      <div className="job-row__actions">
                        <button className="icon-btn" title="Sửa" type="button" onClick={() => handleOpenEdit(job)}>✎</button>
                        <button className="icon-btn icon-btn--danger" title="Xoá" type="button" onClick={() => handleDelete(job.id, job.title)}>🗑</button>
                      </div>
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Modal Thêm / Sửa Job */}
      {modalOpen && (
        <div className="admin-modal">
          <div className="admin-modal__backdrop" onClick={() => setModalOpen(false)} />
          <div className="admin-modal__panel" role="dialog" aria-modal="true">
            <button className="admin-modal__close" aria-label="Đóng" type="button" onClick={() => setModalOpen(false)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
            <div className="admin-modal__body">
              <h2>{editingJob ? 'Sửa vị trí: ' + editingJob.title : 'Thêm vị trí mới'}</h2>
              <form onSubmit={handleSubmit} className="admin-form">
                <fieldset className="admin-fieldset">
                  <legend>Phân loại &amp; hiển thị</legend>
                  <div className="field-row">
                    <label className="field">
                      <span className="field__label">Phòng ban <em>*</em></span>
                      <select
                        value={formData.department}
                        onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                        required
                      >
                        {deptOrder.map((d) => (
                          <option key={d} value={d}>{DEPARTMENT_LABELS[d]}</option>
                        ))}
                      </select>
                    </label>
                    <label className="field">
                      <span className="field__label">Số lượng cần tuyển</span>
                      <input
                        type="number"
                        min="1"
                        value={formData.headcount}
                        onChange={(e) => setFormData({ ...formData, headcount: e.target.value })}
                      />
                    </label>
                  </div>
                  <label className="field">
                    <span className="field__label">Vị trí công việc <em>*</em></span>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Ví dụ: Nhân viên kinh doanh OTC"
                      required
                    />
                  </label>
                  <div className="field-row">
                    <label className="field">
                      <span className="field__label">Icon (cho welcome card)</span>
                      <select
                        value={formData.icon_key}
                        onChange={(e) => setFormData({ ...formData, icon_key: e.target.value })}
                      >
                        <option value="briefcase">💼 briefcase</option>
                        <option value="users">👥 users</option>
                        <option value="calendar">📅 calendar</option>
                        <option value="building">🏢 building</option>
                        <option value="chart">📈 chart</option>
                        <option value="chat">💬 chat</option>
                        <option value="file">📄 file</option>
                        <option value="heart">❤️ heart</option>
                      </select>
                    </label>
                    <label className="field field--toggle">
                      <span className="field__label">Hiển thị public?</span>
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        />
                        <span></span>
                      </label>
                    </label>
                  </div>
                  <details className="admin-disclosure">
                    <summary>Tuỳ chỉnh nâng cao — Slug</summary>
                    <label className="field" style={{ marginTop: '10px' }}>
                      <span className="field__label">Slug</span>
                      <input
                        type="text"
                        value={formData.key}
                        onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                        pattern="[a-z0-9\-]+"
                        placeholder="tự sinh từ tiêu đề"
                      />
                      <small className="hint">Để trống = tự sinh.</small>
                    </label>
                  </details>
                </fieldset>

                <fieldset className="admin-fieldset">
                  <legend>Nội dung JD</legend>
                  <label className="field">
                    <span className="field__label">Mục tiêu công việc <small>(mỗi dòng = 1 bullet)</small></span>
                    <textarea
                      rows="3"
                      value={formData.objectives}
                      onChange={(e) => setFormData({ ...formData, objectives: e.target.value })}
                      placeholder="Thực hiện hoạt động bán hàng để đạt và vượt chỉ tiêu doanh số..."
                    />
                  </label>
                  <label className="field">
                    <span className="field__label">Mô tả công việc <small>(mỗi dòng = 1 bullet)</small></span>
                    <textarea
                      rows="6"
                      value={formData.responsibilities}
                      onChange={(e) => setFormData({ ...formData, responsibilities: e.target.value })}
                      placeholder={'Chăm sóc khách hàng hiện có\nTìm kiếm đại lý mới...'}
                    />
                  </label>
                  <label className="field">
                    <span className="field__label">Yêu cầu <small>(mỗi dòng = 1 bullet)</small></span>
                    <textarea
                      rows="6"
                      value={formData.requirements}
                      onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                      placeholder={'Tốt nghiệp Cao đẳng Dược...\nKinh nghiệm 1 năm...'}
                    />
                  </label>
                  <label className="field">
                    <span className="field__label">Lương thưởng</span>
                    <input
                      type="text"
                      value={formData.salary}
                      onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                      placeholder="Lương cứng 8 - 12 triệu + Thưởng KPI"
                    />
                  </label>
                  <label className="field">
                    <span className="field__label">Quyền lợi <small>(mỗi dòng = 1 bullet)</small></span>
                    <textarea
                      rows="5"
                      value={formData.benefits}
                      onChange={(e) => setFormData({ ...formData, benefits: e.target.value })}
                      placeholder={'Đóng BHXH đầy đủ\nDu lịch thường niên...'}
                    />
                  </label>
                </fieldset>

                <div className="form-actions">
                  <button type="button" className="btn btn--ghost" onClick={() => setModalOpen(false)}>
                    Huỷ
                  </button>
                  <button type="submit" className="btn btn--primary">
                    Lưu
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
